import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { ProgressBar } from './ProgressBar';
import { FiTrendingUp, FiDatabase, FiUsers } from 'react-icons/fi';

interface QuotaData {
  scorings: {
    used: number;
    limit: number;
  };
  storage: {
    used_gb: number;
    limit_gb: number;
  };
  users: {
    active: number;
    limit: number;
  };
}

interface QuotaWidgetProps {
  quotas?: QuotaData;
}

const WidgetContainer = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
`;

const WidgetTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const QuotaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const QuotaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const QuotaHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}20;
  color: ${props => props.color};
  font-size: 20px;
`;

const QuotaInfo = styled.div`
  flex: 1;
`;

const QuotaName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
`;

const QuotaStats = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
  margin-top: ${theme.spacing.xs};
`;

const WarningBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${theme.colors.warning}20;
  color: ${theme.colors.warning};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
  margin-left: ${theme.spacing.sm};
`;

const DangerBadge = styled(WarningBadge)`
  background: ${theme.colors.error}20;
  color: ${theme.colors.error};
`;

const UpgradePrompt = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.primary}10;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-left: 3px solid ${theme.colors.primary};
`;

const UpgradeText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const UpgradeButton = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.accent};
  }
`;

// Default mock data
const defaultQuotas: QuotaData = {
  scorings: { used: 85, limit: 100 },
  storage: { used_gb: 3.2, limit_gb: 5 },
  users: { active: 8, limit: 10 },
};

export const QuotaWidget: React.FC<QuotaWidgetProps> = ({
  quotas = defaultQuotas,
}) => {
  const scoringPercentage = (quotas.scorings.used / quotas.scorings.limit) * 100;
  const storagePercentage = (quotas.storage.used_gb / quotas.storage.limit_gb) * 100;
  const userPercentage = (quotas.users.active / quotas.users.limit) * 100;

  const isNearLimit = scoringPercentage >= 80 || storagePercentage >= 80 || userPercentage >= 80;
  const isOverLimit = scoringPercentage >= 100 || storagePercentage >= 100 || userPercentage >= 100;

  return (
    <WidgetContainer>
      <WidgetTitle>Utilisation des quotas</WidgetTitle>

      <QuotaList>
        <QuotaItem>
          <QuotaHeader>
            <IconWrapper color={theme.colors.primary}>
              <FiTrendingUp />
            </IconWrapper>
            <QuotaInfo>
              <QuotaName>
                Scorings
                {scoringPercentage >= 90 && <DangerBadge>Limite atteinte</DangerBadge>}
                {scoringPercentage >= 70 && scoringPercentage < 90 && (
                  <WarningBadge>Bientôt plein</WarningBadge>
                )}
              </QuotaName>
              <QuotaStats>
                {quotas.scorings.used} / {quotas.scorings.limit} scorings utilisés
              </QuotaStats>
            </QuotaInfo>
          </QuotaHeader>
          <ProgressBar percentage={scoringPercentage} />
        </QuotaItem>

        <QuotaItem>
          <QuotaHeader>
            <IconWrapper color={theme.colors.accent}>
              <FiDatabase />
            </IconWrapper>
            <QuotaInfo>
              <QuotaName>
                Stockage
                {storagePercentage >= 90 && <DangerBadge>Limite atteinte</DangerBadge>}
                {storagePercentage >= 70 && storagePercentage < 90 && (
                  <WarningBadge>Bientôt plein</WarningBadge>
                )}
              </QuotaName>
              <QuotaStats>
                {quotas.storage.used_gb.toFixed(1)} GB / {quotas.storage.limit_gb} GB utilisés
              </QuotaStats>
            </QuotaInfo>
          </QuotaHeader>
          <ProgressBar percentage={storagePercentage} />
        </QuotaItem>

        <QuotaItem>
          <QuotaHeader>
            <IconWrapper color={theme.colors.success}>
              <FiUsers />
            </IconWrapper>
            <QuotaInfo>
              <QuotaName>
                Utilisateurs
                {userPercentage >= 90 && <DangerBadge>Limite atteinte</DangerBadge>}
                {userPercentage >= 70 && userPercentage < 90 && (
                  <WarningBadge>Bientôt plein</WarningBadge>
                )}
              </QuotaName>
              <QuotaStats>
                {quotas.users.active} / {quotas.users.limit} utilisateurs actifs
              </QuotaStats>
            </QuotaInfo>
          </QuotaHeader>
          <ProgressBar percentage={userPercentage} />
        </QuotaItem>
      </QuotaList>

      {(isNearLimit || isOverLimit) && (
        <UpgradePrompt>
          <UpgradeText>
            {isOverLimit
              ? 'Vous avez atteint vos limites. Passez à un plan supérieur.'
              : 'Vous approchez de vos limites. Pensez à upgrader votre plan.'}
          </UpgradeText>
          <UpgradeButton>Voir les plans</UpgradeButton>
        </UpgradePrompt>
      )}
    </WidgetContainer>
  );
};
